import { storiesOf } from '@storybook/react';
import * as React from 'react';
import { VisualTestContainer } from '../../test/visual/VisualTestContainer';
import { Error } from '@wix/wix-ui-icons-common/on-stage';
import { SectionNotification, SectionNotificationProps } from '..';
import { SectionNotificationType, SectionNotificationSize } from './types';
import { classes } from './SectionNotification.visual.st.css';

interface SectionNotificationVisualProps {
  mobile: boolean;
  text: string;
  icon?: React.ReactNode;
  buttons?: string[];
}

class SectionNotificationVisual extends React.Component<
  SectionNotificationVisualProps & SectionNotificationProps
> {
  static defaultProps = {
    mobile: false,
    buttons: [],
  };

  render() {
    const { mobile, icon, text, buttons = [], ...componentProps } = this.props;

    const styles: { maxWidth?: string } = {};
    if (mobile) {
      styles.maxWidth = '400px';
    }

    return (
      <VisualTestContainer>
        <div style={styles}>
          <SectionNotification {...componentProps}>
            {icon ? <SectionNotification.Icon icon={icon} /> : null}
            <SectionNotification.Text>{text}</SectionNotification.Text>
            {buttons.map((title) => (
              <SectionNotification.Button key={title}>
                {title}
              </SectionNotification.Button>
            ))}
          </SectionNotification>
        </div>
      </VisualTestContainer>
    );
  }
}

const textSample = `We're having some trouble authorizing and charging your card. Please make sure it's valid or try a different payment method.`;

function getTests({
  description,
  icon = null,
  buttons = [],
  isMobile = false,
  size = SectionNotificationSize.regular,
}: {
  description: string;
  icon?: React.ReactNode;
  buttons?: string[];
  isMobile?: boolean;
  size?: SectionNotificationSize;
}) {
  return Object.values(SectionNotificationType).map((type) => ({
    it: `${description} - ${type}`,
    props: {
      mobile: isMobile,
      type,
      text: textSample,
      icon,
      buttons,
      size,
    },
  }));
}

function getClassNameTest({ description, className, mobile = false, icon = null}: {description: string; className: string; mobile?: boolean; icon?: React.ReactNode}) {
  return {
    it: description,
    props: {
      mobile,
      className,
      text: textSample,
      icon,
    },
  };
}

const tests = [
  {
    describe: 'desktop',
    its: [
      ...getTests({ description: 'Basic' }),
      ...getTests({
        description: 'Basic compact',
        size: SectionNotificationSize.compact,
      }),
      ...getTests({ description: 'With new icon', icon: <Error /> }),
      ...getTests({ description: 'With button', buttons: ['Approve'] }),
      ...getTests({
        description: 'With icon and two buttons',
        icon: <Error />,
        buttons: ['Approve', 'Decline'],
      }),
    ],
  },
  {
    describe: 'mobile',
    its: [
      ...getTests({ description: 'Basic', isMobile: true }),
      ...getTests({
        description: 'Basic compact',
        isMobile: true,
        size: SectionNotificationSize.compact,
      }),
      ...getTests({
        description: 'With icon',
        icon: <Error />,
        isMobile: true,
      }),
      ...getTests({
        description: 'With button',
        buttons: ['Approve'],
        isMobile: true,
      }),
      ...getTests({
        description: 'With icon and two buttons',
        icon: <Error />,
        buttons: ['Approve', 'Decline'],
        isMobile: true,
      }),
    ],
  },
  {
    describe: 'desktop style overrides',
    its: [
      getClassNameTest({
        description: 'Background color',
        className: classes.backgroundColor,
      }),
      getClassNameTest({
        description: 'Text color',
        className: classes.textColor,
      }),
      getClassNameTest({
        description: 'Border radius',
        className: classes.borderRadius,
      }),
      getClassNameTest({
        description: 'Icon color',
        className: classes.iconColor,
        icon: <Error />,
      }),
      getClassNameTest({
        description: 'WST text color',
        className: classes.wstParagraph2Color,
      }),
    ],
  },
  {
    describe: 'mobile style overrides',
    its: [
      getClassNameTest({
        description: 'Background color',
        className: classes.backgroundColor,
        mobile: true,
      }),
      getClassNameTest({
        description: 'Text color',
        className: classes.textColor,
        mobile: true,
      }),
      getClassNameTest({
        description: 'Border radius',
        className: classes.borderRadius,
        mobile: true,
      }),
      getClassNameTest({
        description: 'Icon color',
        className: classes.iconColor,
        icon: <Error />,
      }),
      getClassNameTest({
        description: 'WST text color',
        className: classes.wstParagraph2Color,
        mobile: true,
      }),
    ],
  },
];

tests.forEach(({ describe, its }) => {
  its.forEach(({ it, props }) => {
    storiesOf(`SectionNotification/${describe}`, module).add(it, () => (
      <SectionNotificationVisual {...props} />
    ));
  });
});
